import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import AnimalPhotoGallery from './AnimalPhotoGallery';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './tabs';
import { 
  Users, Plus, Edit2, Trash2, Calendar, Scale, Heart, 
  Milk, ClipboardList, ChevronRight, ChevronDown, ChevronUp,
  X, Tag, GitBranch, User, Baby, Camera, FileText, Loader2, Upload, ImageIcon, Star
} from 'lucide-react';
import { validateDate, validateQuantity, maxWeightForUnit } from '../../utils/validation';
import { todayLocal, formatDateShort } from '../../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to safely parse API error responses (handles FastAPI/Pydantic validation errors)
const parseApiError = (error, defaultMessage = 'An error occurred') => {
  if (!error.response?.data?.detail) return defaultMessage;
  const detail = error.response.data.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // Pydantic validation errors come as an array of {type, loc, msg, input, url}
    return detail.map(e => e.msg || e.message || String(e)).join(', ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return defaultMessage;
};

const SEX_OPTIONS = [
  { value: 'female', label: 'Female', symbol: '♀', color: 'text-pink-500' },
  { value: 'male', label: 'Male', symbol: '♂', color: 'text-blue-500' },
  { value: 'unknown', label: 'Unknown', symbol: '?', color: 'text-gray-500' }
];

const PURPOSE_OPTIONS = [
  { value: 'milk', label: 'Milk' },
  { value: 'meat', label: 'Meat' },
  { value: 'breeding', label: 'Breeding' },
  { value: 'mixed', label: 'Mixed' }
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-700' },
  { value: 'sold', label: 'Sold', color: 'bg-blue-100 text-blue-700' },
  { value: 'culled', label: 'Culled', color: 'bg-orange-100 text-orange-700' },
  { value: 'deceased', label: 'Deceased', color: 'bg-gray-100 text-gray-700' }
];

const EVENT_TYPES = [
  { value: 'health', label: 'Health', icon: Heart, color: 'bg-red-100 text-red-700' },
  { value: 'weight', label: 'Weight', icon: Scale, color: 'bg-blue-100 text-blue-700' },
  { value: 'breeding', label: 'Breeding', icon: Baby, color: 'bg-pink-100 text-pink-700' },
  { value: 'note', label: 'Note', icon: ClipboardList, color: 'bg-gray-100 text-gray-700' }
];

// Birth Event Outcomes - Farm-friendly language
const BABY_OUTCOMES = [
  { value: 'thriving_with_mom', label: 'Thriving with mom', color: 'bg-green-100 text-green-700', allowProfile: true },
  { value: 'needed_help_nursing', label: 'Needed help nursing', color: 'bg-yellow-100 text-yellow-700', allowProfile: true },
  { value: 'bottle_baby', label: 'Bottle baby', color: 'bg-orange-100 text-orange-700', allowProfile: true },
  { value: 'fostered', label: 'Fostered', color: 'bg-blue-100 text-blue-700', allowProfile: true },
  { value: 'stillborn', label: 'Stillborn', color: 'bg-gray-200 text-gray-600', allowProfile: false },
  { value: 'did_not_make_it', label: 'Did not make it after birth', color: 'bg-gray-200 text-gray-600', allowProfile: false },
  { value: 'unknown', label: 'Unknown or other', color: 'bg-gray-100 text-gray-500', allowProfile: true }
];

const FoundationStock = ({ 
  animalType, // "goat", "sheep", "cattle"
  groupId,    // herd_id
  groupName,  // name of the herd for display
  getAuthHeader,
  breeds = []
}) => {
  const [animals, setAnimals] = useState([]);
  const [allAnimals, setAllAnimals] = useState([]); // For parent selection
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('profile'); // 'profile' or 'lineage'
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingAnimal, setEditingAnimal] = useState(null);
  
  // Lineage state
  const [lineageData, setLineageData] = useState(null);
  const [expandedOffspring, setExpandedOffspring] = useState({});
  
  // Form states
  const [animalForm, setAnimalForm] = useState({
    name: '',
    breed: '',
    sex: 'unknown',
    birth_date: '',
    tag_number: '',
    photo_url: '',
    purpose: 'mixed',
    dam_id: '',
    sire_id: '',
    notes: ''
  });
  
  const [eventForm, setEventForm] = useState({
    event_type: 'note',
    description: '',
    value: '',
    unit: '',
    date: todayLocal(),
    notes: ''
  });

  // Birth Event state
  const [showBirthDialog, setShowBirthDialog] = useState(false);
  const [birthEvents, setBirthEvents] = useState([]);
  const [birthStats, setBirthStats] = useState(null);
  const [showBirthHistoryDialog, setShowBirthHistoryDialog] = useState(false);
  const [damBirthHistory, setDamBirthHistory] = useState(null);
  const [birthForm, setBirthForm] = useState({
    dam_id: '',
    sire_id: '',
    date: todayLocal(),
    time: '',
    notes: '',
    babies: [{ outcome: 'thriving_with_mom', sex: '', birth_weight: '', weight_unit: 'lbs', notes: '', create_profile: false, name: '' }]
  });

  // Breeding Event state
  const [showBreedingDialog, setShowBreedingDialog] = useState(false);
  const [breedingEvents, setBreedingEvents] = useState([]);
  const [breedingForm, setBreedingForm] = useState({
    dam_id: '',
    sire_id: '',
    date: todayLocal(),
    notes: '',
    confirmed: false
  });

  // Quick Action Dialog States
  const [showQuickMilkDialog, setShowQuickMilkDialog] = useState(false);
  const [showQuickWeightDialog, setShowQuickWeightDialog] = useState(false);
  const [showQuickPhotoDialog, setShowQuickPhotoDialog] = useState(false);
  const [showQuickNoteDialog, setShowQuickNoteDialog] = useState(false);
  const [quickMilkForm, setQuickMilkForm] = useState({
    date: todayLocal(),
    amount: '',
    unit: 'gallons',
    notes: ''
  });
  const [quickWeightForm, setQuickWeightForm] = useState({
    date: todayLocal(),
    weight: '',
    unit: 'lbs',
    notes: ''
  });
  const [quickPhotoForm, setQuickPhotoForm] = useState({
    photo_url: '',
    date: todayLocal(),
    notes: ''
  });
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [quickNoteForm, setQuickNoteForm] = useState({
    date: todayLocal(),
    notes: ''
  });

  useEffect(() => {
    fetchAnimals();
    fetchAllAnimals();
  }, [animalType, groupId]);

  // Check for deep link to open specific animal
  useEffect(() => {
    // Check immediately
    const checkForDeepLink = () => {
      const openAnimalId = localStorage.getItem('openFoundationAnimal');
      if (openAnimalId && animals.length > 0) {
        localStorage.removeItem('openFoundationAnimal');
        // Check if this animal belongs to this group
        const animal = animals.find(a => a.id === openAnimalId);
        if (animal) {
          fetchAnimalDetail(openAnimalId);
          return true;
        }
      }
      return false;
    };
    
    // Try immediately
    if (checkForDeepLink()) return;
    
    // Also set up an interval to check for a few seconds
    // This handles the timing issue where localStorage is set after initial check
    let attempts = 0;
    const maxAttempts = 10;
    const intervalId = setInterval(() => {
      attempts++;
      if (checkForDeepLink() || attempts >= maxAttempts) {
        clearInterval(intervalId);
      }
    }, 300);
    
    return () => clearInterval(intervalId);
  }, [animals]);

  const fetchAnimals = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/animals/foundation?animal_type=${animalType}`;
      if (groupId) {
        url += `&group_id=${groupId}`;
      }
      const res = await axios.get(url, { headers: getAuthHeader() });
      setAnimals(res.data.animals || []);
    } catch (error) {
      console.error('Failed to fetch foundation animals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllAnimals = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/animals/foundation?animal_type=${animalType}&include_inactive=true`,
        { headers: getAuthHeader() }
      );
      setAllAnimals(res.data.animals || []);
    } catch (error) {
      console.error('Failed to fetch all animals:', error);
    }
  };

  const fetchAnimalDetail = async (animalId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/animals/foundation/${animalId}`,
        { headers: getAuthHeader() }
      );
      setSelectedAnimal(res.data);
      setShowDetailDialog(true);
      setActiveView('profile');
    } catch (error) {
      console.error('Failed to load animal details:', error.response?.status, error.response?.data);
      // Try legacy endpoint as fallback
      try {
        const legacyRes = await axios.get(
          `${API_URL}/api/animals/individual/${animalId}`,
          { headers: getAuthHeader() }
        );
        // Map legacy fields
        const animal = legacyRes.data;
        if (!animal.sex) animal.sex = animal.gender || 'unknown';
        if (!animal.purpose) animal.purpose = 'mixed';
        animal._legacy = true;
        setSelectedAnimal(animal);
        setShowDetailDialog(true);
        setActiveView('profile');
      } catch (legacyError) {
        console.error('Legacy fallback also failed:', legacyError.response?.status);
        toast.error('Failed to load animal details');
      }
    }
  };

  const fetchLineage = async (animalId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/animals/foundation/${animalId}/lineage`,
        { headers: getAuthHeader() }
      );
      setLineageData(res.data);
    } catch (error) {
      toast.error('Failed to load lineage');
    }
  };

  const handleCreateAnimal = async () => {
    const breedName = animalForm.breed === '__custom__' ? animalForm.customBreed : animalForm.breed;
    if (!animalForm.name) {
      toast.error('Please enter a name');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/animals/foundation`, {
        ...animalForm,
        breed: breedName || '',
        animal_type: animalType,
        group_id: groupId,
        dam_id: animalForm.dam_id || null,
        sire_id: animalForm.sire_id || null
      }, { headers: getAuthHeader() });
      
      toast.success('Animal added to Foundation Stock');
      setShowAddDialog(false);
      resetAnimalForm();
      fetchAnimals();
      fetchAllAnimals();
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to add animal'));
    }
  };

  const handleUpdateAnimal = async () => {
    const breedName = animalForm.breed === '__custom__' ? animalForm.customBreed : animalForm.breed;
    try {
      await axios.put(
        `${API_URL}/api/animals/foundation/${editingAnimal.id}`,
        {
          ...animalForm,
          breed: breedName || '',
          dam_id: animalForm.dam_id || null,
          sire_id: animalForm.sire_id || null
        },
        { headers: getAuthHeader() }
      );
      toast.success('Animal updated');
      setShowAddDialog(false);
      setEditingAnimal(null);
      resetAnimalForm();
      fetchAnimals();
      fetchAllAnimals();
      if (selectedAnimal?.id === editingAnimal.id) {
        fetchAnimalDetail(editingAnimal.id);
        // Clear cached lineage so it refreshes on next view
        setLineageData(null);
      }
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to update animal'));
    }
  };

  const handleDeleteAnimal = async (animalId) => {
    if (!window.confirm('Remove this animal from Foundation Stock? Lineage references and logs will be preserved.')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/animals/foundation/${animalId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Animal removed');
      setShowDetailDialog(false);
      setSelectedAnimal(null);
      fetchAnimals();
    } catch (error) {
      toast.error('Failed to remove animal');
    }
  };

  // Toggle Foundation Star status
  const handleToggleFoundation = async (animal) => {
    const newFoundationStatus = !(animal.is_foundation === true || animal.is_foundation === undefined);
    try {
      await axios.put(
        `${API_URL}/api/animals/foundation/${animal.id}`,
        { is_foundation: newFoundationStatus },
        { headers: getAuthHeader() }
      );
      toast.success(newFoundationStatus 
        ? `${animal.name} added to Foundation Animals` 
        : `${animal.name} removed from Foundation Animals`
      );
      fetchAnimals();
      fetchAllAnimals();
      // Update selected animal if viewing
      if (selectedAnimal?.id === animal.id) {
        setSelectedAnimal(prev => ({ ...prev, is_foundation: newFoundationStatus }));
      }
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to update animal'));
    }
  };

  const handleAddEvent = async () => {
    if (!eventForm.description) {
      toast.error('Please enter a description');
      return;
    }
    // Same validation surfaces as Milk / Weight / Health so backend caps stay
    // in sync with a specific, non-generic error toast.
    const etype = (eventForm.event_type || '').toLowerCase();
    if (etype === 'weight') {
      const q = validateQuantity(eventForm.value, { field: 'Weight', max: maxWeightForUnit(eventForm.unit) });
      if (!q.ok) { toast.error(q.message); return; }
    } else if (etype === 'milk') {
      const q = validateQuantity(eventForm.value, { field: 'Quantity', max: 50 });
      if (!q.ok) { toast.error(q.message); return; }
    }
    const dv = validateDate(eventForm.date, { field: 'Date' });
    if (!dv.ok && dv.reason !== 'too_old') { toast.error(dv.message); return; }

    try {
      await axios.post(
        `${API_URL}/api/animals/foundation/${selectedAnimal.id}/events`,
        { ...eventForm, allow_historical: !dv.ok && dv.reason === 'too_old' },
        { headers: getAuthHeader() }
      );
      toast.success('Event logged');
      setShowEventDialog(false);
      resetEventForm();
      fetchAnimalDetail(selectedAnimal.id);
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to log event'));
    }
  };

  const resetAnimalForm = () => {
    setAnimalForm({
      name: '',
      breed: '',
      customBreed: '',
      sex: 'unknown',
      birth_date: '',
      tag_number: '',
      photo_url: '',
      purpose: 'mixed',
      dam_id: '',
      sire_id: '',
      notes: ''
    });
  };

  const resetEventForm = () => {
    setEventForm({
      event_type: 'note',
      description: '',
      value: '',
      unit: '',
      date: todayLocal(),
      notes: ''
    });
  };

  // Birth Event Functions
  const resetBirthForm = () => {
    setBirthForm({
      dam_id: '',
      sire_id: '',
      date: todayLocal(),
      time: '',
      notes: '',
      babies: [{ outcome: 'thriving_with_mom', sex: '', birth_weight: '', weight_unit: 'lbs', notes: '', create_profile: false, name: '' }]
    });
  };

  const fetchBirthStats = async () => {
    try {
      const params = new URLSearchParams();
      if (animalType) params.append('animal_type', animalType);
      if (groupId) params.append('group_id', groupId);
      
      const res = await axios.get(
        `${API_URL}/api/birth-events/stats/season?${params.toString()}`,
        { headers: getAuthHeader() }
      );
      setBirthStats(res.data);
    } catch (error) {
      console.error('Failed to fetch birth stats:', error);
    }
  };

  const fetchBirthEvents = async () => {
    try {
      const params = new URLSearchParams();
      if (animalType) params.append('animal_type', animalType);
      if (groupId) params.append('group_id', groupId);
      params.append('limit', '50');
      
      const res = await axios.get(
        `${API_URL}/api/birth-events?${params.toString()}`,
        { headers: getAuthHeader() }
      );
      setBirthEvents(res.data.events || []);
    } catch (error) {
      console.error('Failed to fetch birth events:', error);
    }
  };

  const fetchDamBirthHistory = async (damId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/birth-events/dam/${damId}`,
        { headers: getAuthHeader() }
      );
      setDamBirthHistory(res.data);
      setShowBirthHistoryDialog(true);
    } catch (error) {
      toast.error('Failed to load birth history');
    }
  };

  const addBabyEntry = () => {
    setBirthForm(prev => ({
      ...prev,
      babies: [...prev.babies, { outcome: 'thriving_with_mom', sex: '', birth_weight: '', weight_unit: 'lbs', notes: '', create_profile: false, name: '' }]
    }));
  };

  const removeBabyEntry = (index) => {
    if (birthForm.babies.length <= 1) return;
    setBirthForm(prev => ({
      ...prev,
      babies: prev.babies.filter((_, i) => i !== index)
    }));
  };

  const updateBabyEntry = (index, field, value) => {
    setBirthForm(prev => {
      const newBabies = [...prev.babies];
      newBabies[index] = { ...newBabies[index], [field]: value };
      
      // If outcome changes to one that doesn't allow profiles, reset create_profile
      if (field === 'outcome') {
        const outcomeInfo = BABY_OUTCOMES.find(o => o.value === value);
        if (outcomeInfo && !outcomeInfo.allowProfile) {
          newBabies[index].create_profile = false;
        }
      }
      
      return { ...prev, babies: newBabies };
    });
  };

  const handleCreateBirthEvent = async () => {
    if (!birthForm.dam_id) {
      toast.error('Please select the mother');
      return;
    }
    if (!birthForm.date) {
      toast.error('Please enter the birth date');
      return;
    }
    // Date validation with silent historical bypass — full-blown historical
    // confirm dialog for THIS form (multi-baby) is planned; for now we
    // gracefully accept old dates by flipping allow_historical.
    const dv = validateDate(birthForm.date, { field: 'Birth date' });
    if (!dv.ok && dv.reason !== 'too_old') { toast.error(dv.message); return; }
    // Per-baby weight sanity check — backend re-validates.
    for (let i = 0; i < birthForm.babies.length; i++) {
      const b = birthForm.babies[i];
      if (b.birth_weight !== '' && b.birth_weight != null) {
        const w = Number(b.birth_weight);
        if (!Number.isFinite(w) || w <= 0) { toast.error(`Baby ${i + 1} weight must be greater than 0`); return; }
        const cap = maxWeightForUnit(b.weight_unit) / 6;
        if (w > cap) { toast.error(`Baby ${i + 1} weight looks too high (max ${cap} ${b.weight_unit || 'lbs'})`); return; }
      }
    }
    
    try {
      // Clean up form data - convert empty strings to null for optional numeric fields
      const cleanedForm = {
        ...birthForm,
        babies: birthForm.babies.map(baby => ({
          ...baby,
          birth_weight: baby.birth_weight === '' ? null : parseFloat(baby.birth_weight) || null,
          sex: baby.sex || null,
          notes: baby.notes || null,
          name: baby.name || null
        })),
        sire_id: birthForm.sire_id || null,
        time: birthForm.time || null,
        notes: birthForm.notes || null,
        allow_historical: !dv.ok && dv.reason === 'too_old'
      };
      
      const res = await axios.post(
        `${API_URL}/api/birth-events`,
        cleanedForm,
        { headers: getAuthHeader() }
      );
      
      const createdCount = res.data.created_animals?.length || 0;
      const babyCount = birthForm.babies.length;
      
      toast.success(
        createdCount > 0 
          ? `Birth logged! ${createdCount} new animal profile${createdCount > 1 ? 's' : ''} created.`
          : `Birth logged! ${babyCount} ${babyCount === 1 ? 'baby' : 'babies'} recorded.`
      );
      
      setShowBirthDialog(false);
      resetBirthForm();
      fetchAnimals();
      fetchAllAnimals();
      fetchBirthEvents();
      fetchBirthStats();
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to log birth event'));
    }
  };

  const openBirthDialogForDam = (dam) => {
    resetBirthForm();
    setBirthForm(prev => ({
      ...prev,
      dam_id: dam.id
    }));
    setShowBirthDialog(true);
  };

  // Breeding Event Functions
  const resetBreedingForm = () => {
    setBreedingForm({
      dam_id: '',
      sire_id: '',
      date: todayLocal(),
      notes: '',
      confirmed: false
    });
  };

  const fetchBreedingEvents = async (damId) => {
    try {
      const url = damId 
        ? `${API_URL}/api/breeding-events/dam/${damId}`
        : `${API_URL}/api/breeding-events?animal_type=${animalType}`;
      const res = await axios.get(url, { headers: getAuthHeader() });
      setBreedingEvents(res.data.events || []);
    } catch (error) {
      console.error('Failed to fetch breeding events:', error);
    }
  };

  const handleCreateBreedingEvent = async () => {
    if (!breedingForm.dam_id) {
      toast.error('Please select the female being bred');
      return;
    }
    if (!breedingForm.date) {
      toast.error('Please enter the breeding date');
      return;
    }
    
    try {
      const res = await axios.post(
        `${API_URL}/api/breeding-events`,
        breedingForm,
        { headers: getAuthHeader() }
      );
      
      const dueDate = new Date(res.data.due_date).toLocaleDateString();
      toast.success(`Breeding logged! Expected due date: ${dueDate}`);
      
      setShowBreedingDialog(false);
      resetBreedingForm();
      fetchAnimals();
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to log breeding event'));
    }
  };

  const openBreedingDialogForDam = (dam) => {
    resetBreedingForm();
    setBreedingForm(prev => ({
      ...prev,
      dam_id: dam.id
    }));
    setShowBreedingDialog(true);
  };

  // Quick Action Handlers for Foundation Animals
  const handleQuickMilkLog = async () => {
    if (!selectedAnimal) { toast.error('No animal selected'); return; }
    const q = validateQuantity(quickMilkForm.amount, { field: 'Quantity', max: 50 });
    if (!q.ok) { toast.error(q.message); return; }
    const dv = validateDate(quickMilkForm.date, { field: 'Date' });
    if (!dv.ok && dv.reason !== 'too_old') { toast.error(dv.message); return; }
    try {
      // Step 5 (Feb 2026) fix: the previous payload sent `amount` instead of
      // `quantity` and omitted required `animal_id` + `animal_type`, causing
      // a Pydantic 422 on every individual-animal milk log. Now matches the
      // MilkProductionLog model exactly.
      await axios.post(`${API_URL}/api/livestock/milk-production`, {
        animal_id: selectedAnimal.id,
        animal_type: selectedAnimal.animal_type || 'goat',
        herd_id: selectedAnimal.group_id,
        foundation_animal_id: selectedAnimal.id,
        quantity: parseFloat(quickMilkForm.amount),
        unit: quickMilkForm.unit,
        date: quickMilkForm.date,
        notes: quickMilkForm.notes,
        allow_historical: !dv.ok && dv.reason === 'too_old'
      }, { headers: getAuthHeader() });
      toast.success(`Milk logged for ${selectedAnimal.name}!`);
      setShowQuickMilkDialog(false);
      setQuickMilkForm({ date: todayLocal(), amount: '', unit: 'gallons', notes: '' });
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to log milk'));
    }
  };

  const handleQuickWeightLog = async () => {
    if (!selectedAnimal) { toast.error('No animal selected'); return; }
    const q = validateQuantity(quickWeightForm.weight, { field: 'Weight', max: maxWeightForUnit(quickWeightForm.unit) });
    if (!q.ok) { toast.error(q.message); return; }
    const dv = validateDate(quickWeightForm.date, { field: 'Date' });
    if (!dv.ok && dv.reason !== 'too_old') { toast.error(dv.message); return; }
    try {
      await axios.post(`${API_URL}/api/animals/foundation/${selectedAnimal.id}/events`, {
        event_type: 'weight',
        description: `Weight: ${quickWeightForm.weight} ${quickWeightForm.unit}`,
        value: quickWeightForm.weight,
        unit: quickWeightForm.unit,
        date: quickWeightForm.date,
        notes: quickWeightForm.notes
      }, { headers: getAuthHeader() });
      toast.success(`Weight logged for ${selectedAnimal.name}!`);
      setShowQuickWeightDialog(false);
      setQuickWeightForm({ date: todayLocal(), weight: '', unit: 'lbs', notes: '' });
      fetchAnimalDetail(selectedAnimal.id);
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to log weight'));
    }
  };

  // Compress image before upload (for mobile photos which can be very large)
  const compressImage = async (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Scale down if needed
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => resolve(blob),
            'image/jpeg',
            quality
          );
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle file selection from phone gallery
  const handlePhotoFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Create preview immediately
    const previewUrl = URL.createObjectURL(file);
    setPhotoPreview(previewUrl);
    
    setPhotoUploading(true);
    try {
      // Compress if larger than 1MB
      let fileToUpload = file;
      if (file.size > 1024 * 1024) {
        const compressed = await compressImage(file);
        fileToUpload = new File([compressed], file.name, { type: 'image/jpeg' });
      }
      
      // Upload to backend
      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('category', 'livestock');
      
      const response = await axios.post(`${API_URL}/api/upload/photo`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Build full URL
      const photoUrl = response.data.url.startsWith('http') ? response.data.url : `${API_URL}${response.data.url}`;
      setQuickPhotoForm(prev => ({ ...prev, photo_url: photoUrl }));
      toast.success('Photo uploaded!');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload photo. Please try again.');
      setPhotoPreview(null);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleQuickPhotoAdd = async () => {
    if (!selectedAnimal || !quickPhotoForm.photo_url) {
      toast.error('Please select a photo');
      return;
    }
    try {
      // Update the animal's photo_url
      await axios.put(`${API_URL}/api/animals/foundation/${selectedAnimal.id}`, {
        photo_url: quickPhotoForm.photo_url
      }, { headers: getAuthHeader() });
      
      // Also log as a photo event for history
      await axios.post(`${API_URL}/api/animals/foundation/${selectedAnimal.id}/events`, {
        event_type: 'note',
        description: `Photo added`,
        date: quickPhotoForm.date,
        notes: quickPhotoForm.notes || 'Profile photo updated',
        photo_url: quickPhotoForm.photo_url
      }, { headers: getAuthHeader() });
      
      // Also update animal's main photo if they don't have one
      if (!selectedAnimal.photo_url) {
        await axios.put(`${API_URL}/api/animals/foundation/${selectedAnimal.id}`, {
          photo_url: quickPhotoForm.photo_url
        }, { headers: getAuthHeader() });
      }
      
      toast.success(`Photo added for ${selectedAnimal.name}!`);
      setShowQuickPhotoDialog(false);
      setQuickPhotoForm({ photo_url: '', date: todayLocal(), notes: '' });
      fetchAnimalDetail(selectedAnimal.id);
    } catch (error) {
      toast.error('Failed to add photo');
    }
  };

  const handleQuickNoteAdd = async () => {
    if (!selectedAnimal || !quickNoteForm.notes) {
      toast.error('Please enter a note');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/animals/foundation/${selectedAnimal.id}/events`, {
        event_type: 'note',
        description: quickNoteForm.notes.substring(0, 100),
        date: quickNoteForm.date,
        notes: quickNoteForm.notes
      }, { headers: getAuthHeader() });
      toast.success(`Note added for ${selectedAnimal.name}!`);
      setShowQuickNoteDialog(false);
      setQuickNoteForm({ date: todayLocal(), notes: '' });
      fetchAnimalDetail(selectedAnimal.id);
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const getOutcomeDisplay = (outcome) => {
    const info = BABY_OUTCOMES.find(o => o.value === outcome);
    return info ? (
      <span className={`text-xs px-2 py-0.5 rounded ${info.color}`}>
        {info.label}
      </span>
    ) : outcome;
  };

  const openEditDialog = (animal) => {
    setEditingAnimal(animal);
    // Check if the breed is a custom one (not in the breeds list)
    const isCustomBreed = animal.breed && breeds.length > 0 && !breeds.includes(animal.breed);
    setAnimalForm({
      name: animal.name,
      breed: isCustomBreed ? '__custom__' : (animal.breed || ''),
      customBreed: isCustomBreed ? animal.breed : '',
      sex: animal.sex || 'unknown',
      birth_date: animal.birth_date || '',
      tag_number: animal.tag_number || '',
      photo_url: animal.photo_url || '',
      purpose: animal.purpose || 'mixed',
      dam_id: animal.dam_id || '',
      sire_id: animal.sire_id || '',
      notes: animal.notes || ''
    });
    setShowAddDialog(true);
  };

  const getSexDisplay = (sex) => {
    const option = SEX_OPTIONS.find(s => s.value === sex);
    return option ? (
      <span className={option.color}>{option.symbol}</span>
    ) : null;
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status);
    return opt ? (
      <span className={`text-xs px-1.5 py-0.5 rounded ${opt.color}`}>
        {opt.label}
      </span>
    ) : null;
  };

  const getPotentialDams = () => {
    return allAnimals.filter(a => 
      a.sex === 'female' && 
      a.id !== editingAnimal?.id
    );
  };

  const getPotentialSires = () => {
    return allAnimals.filter(a => 
      a.sex === 'male' && 
      a.id !== editingAnimal?.id
    );
  };

  // Lineage Tree Component
  const LineageTree = ({ data }) => {
    if (!data) return null;

    const ParentNode = ({ parent, label }) => {
      if (!parent) {
        return (
          <div className="p-2 rounded bg-muted/30 text-sm text-muted-foreground">
            {label}: Unknown
          </div>
        );
      }
      return (
        <div 
          className="p-2 rounded bg-muted/50 border cursor-pointer hover:border-primary/50"
          onClick={() => fetchAnimalDetail(parent.id)}
        >
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{parent.name}</span>
            {getSexDisplay(parent.sex)}
          </div>
          <p className="text-xs text-muted-foreground">
            {parent.tag_number && `#${parent.tag_number} • `}
            {parent.breed || 'Unknown breed'}
          </p>
          {parent.status !== 'active' && getStatusBadge(parent.status)}
        </div>
      );
    };

    return (
      <div className="space-y-4">
        {/* Parents */}
        <div>
          <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
            <GitBranch className="w-4 h-4" />
            Parents
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <ParentNode parent={data.dam} label="Dam (Mother)" />
            <ParentNode parent={data.sire} label="Sire (Father)" />
          </div>
        </div>

        {/* Offspring */}
        <div>
          <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
            <Baby className="w-4 h-4" />
            Offspring ({data.offspring?.length || 0})
          </h4>
          {!data.offspring || data.offspring.length === 0 ? (
            <p className="text-sm text-muted-foreground">No offspring recorded</p>
          ) : (
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {data.offspring.map((child) => (
                <div 
                  key={child.id}
                  className="p-2 rounded bg-muted/30 border cursor-pointer hover:border-primary/50 flex items-center justify-between"
                  onClick={() => fetchAnimalDetail(child.id)}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{child.name}</span>
                      {getSexDisplay(child.sex)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {child.tag_number && `#${child.tag_number} • `}
                      {child.birth_date && new Date(child.birth_date).toLocaleDateString()}
                    </p>
                  </div>
                  {child.status !== 'active' && getStatusBadge(child.status)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <Users className="w-5 h-5 text-primary shrink-0" />
          <h3 className="font-heading font-semibold text-base">Foundation Stock</h3>
        </div>
        <Button 
          size="sm"
          className="shrink-0"
          onClick={() => { resetAnimalForm(); setEditingAnimal(null); setShowAddDialog(true); }}
          data-testid="add-foundation-animal-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add
        </Button>
      </div>

      {/* Animal List */}
      {animals.length === 0 ? (
        <Card className="card-farm">
          <CardContent className="py-8 text-center">
            <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground mb-3">
              No Foundation Stock animals yet
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Track individual animals with lineage, health, and production records.
            </p>
            <Button 
              variant="outline" 
              onClick={() => setShowAddDialog(true)}
              data-testid="add-first-foundation-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Animal
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {animals.map((animal) => (
            <Card 
              key={animal.id}
              className="card-farm cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fetchAnimalDetail(animal.id)}
              data-testid={`foundation-animal-${animal.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {animal.photo_url ? (
                    <img 
                      src={animal.photo_url} 
                      alt={animal.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-primary/20"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-primary/5 border-2 border-primary/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium truncate">{animal.name}</h4>
                      {getSexDisplay(animal.sex)}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {animal.breed || 'Unknown breed'}
                      {animal.tag_number && ` • #${animal.tag_number}`}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground capitalize">{animal.purpose}</span>
                      {animal.status !== 'active' && getStatusBadge(animal.status)}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleFoundation(animal);
                    }}
                    className={`p-1.5 rounded-full transition-colors ${
                      animal.is_foundation === true || animal.is_foundation === undefined
                        ? 'text-amber-500 hover:bg-amber-500/10' 
                        : 'text-muted-foreground/40 hover:text-amber-500 hover:bg-amber-500/10'
                    }`}
                    title={animal.is_foundation === true || animal.is_foundation === undefined ? 'Remove from Foundation' : 'Add to Foundation'}
                    data-testid={`foundation-star-${animal.id}`}
                  >
                    <Star className={`w-5 h-5 ${animal.is_foundation === true || animal.is_foundation === undefined ? 'fill-current' : ''}`} />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Animal Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              {editingAnimal ? 'Edit Animal' : 'Add Foundation Animal'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-sm">Name *</Label>
              <Input
                value={animalForm.name}
                onChange={(e) => setAnimalForm({ ...animalForm, name: e.target.value })}
                placeholder="e.g., Daisy, Champion"
                className="h-9"
                data-testid="foundation-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Breed</Label>
                {breeds.length > 0 ? (
                  <>
                    <select
                      className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                      value={animalForm.breed === '__custom__' ? '__custom__' : (breeds.includes(animalForm.breed) ? animalForm.breed : (animalForm.breed ? '__custom__' : ''))}
                      onChange={(e) => {
                        if (e.target.value === '__custom__') {
                          setAnimalForm({ ...animalForm, breed: '__custom__', customBreed: '' });
                        } else {
                          setAnimalForm({ ...animalForm, breed: e.target.value, customBreed: '' });
                        }
                      }}
                      data-testid="foundation-breed-select"
                    >
                      <option value="">Select...</option>
                      {breeds.map(b => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                      <option value="__custom__">✨ Custom / Hybrid</option>
                    </select>
                    {(animalForm.breed === '__custom__' || (animalForm.breed && !breeds.includes(animalForm.breed))) && (
                      <Input
                        className="mt-1 h-8"
                        placeholder="Custom breed name"
                        value={animalForm.customBreed || (animalForm.breed !== '__custom__' ? animalForm.breed : '')}
                        onChange={(e) => setAnimalForm({ ...animalForm, customBreed: e.target.value, breed: '__custom__' })}
                        data-testid="foundation-custom-breed-input"
                      />
                    )}
                  </>
                ) : (
                  <Input
                    value={animalForm.breed}
                    onChange={(e) => setAnimalForm({ ...animalForm, breed: e.target.value })}
                    placeholder="Breed name"
                    className="h-9"
                  />
                )}
              </div>
              <div>
                <Label className="text-sm">Sex</Label>
                <select
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  value={animalForm.sex}
                  onChange={(e) => setAnimalForm({ ...animalForm, sex: e.target.value })}
                  data-testid="foundation-sex-select"
                >
                  {SEX_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.symbol} {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Birth Date</Label>
                <Input
                  type="date"
                  value={animalForm.birth_date}
                  onChange={(e) => setAnimalForm({ ...animalForm, birth_date: e.target.value })}
                  className="h-9"
                  data-testid="foundation-birth-date"
                />
              </div>
              <div>
                <Label className="text-sm">Tag/ID #</Label>
                <Input
                  value={animalForm.tag_number}
                  onChange={(e) => setAnimalForm({ ...animalForm, tag_number: e.target.value })}
                  placeholder="e.g., 001"
                  className="h-9"
                  data-testid="foundation-tag-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Purpose</Label>
                <select
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  value={animalForm.purpose}
                  onChange={(e) => setAnimalForm({ ...animalForm, purpose: e.target.value })}
                  data-testid="foundation-purpose-select"
                >
                  {PURPOSE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-sm">Status</Label>
                <select
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  value={editingAnimal?.status || 'active'}
                  onChange={(e) => setAnimalForm({ ...animalForm, status: e.target.value })}
                  disabled={!editingAnimal}
                  data-testid="foundation-status-select"
                >
                  {STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Photo Upload */}
            <div>
              <Label className="text-sm flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Photo (Optional)
              </Label>
              {animalForm.photo_url ? (
                <div className="relative rounded-lg overflow-hidden border mt-2">
                  <img 
                    src={animalForm.photo_url} 
                    alt="Animal preview" 
                    className="w-full h-32 object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  <button
                    onClick={() => setAnimalForm({ ...animalForm, photo_url: '' })}
                    className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full text-white hover:bg-black/80 transition-colors"
                    type="button"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label 
                  htmlFor="add-animal-photo" 
                  className="flex flex-col items-center justify-center w-full h-24 mt-2 border-2 border-dashed border-muted-foreground/30 rounded-lg cursor-pointer hover:border-primary/50 hover:bg-muted/20 transition-colors active:bg-muted/30"
                  onClick={() => document.getElementById('add-animal-photo')?.click()}
                >
                  <input
                    id="add-animal-photo"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (!file.type.startsWith('image/')) {
                        toast.error('Please select an image');
                        return;
                      }
                      const formData = new FormData();
                      formData.append('file', file);
                      formData.append('category', 'livestock');
                      try {
                        const res = await axios.post(`${API_URL}/api/upload/photo`, formData, {
                          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                        });
                        setAnimalForm({ ...animalForm, photo_url: res.data.url.startsWith('http') ? res.data.url : `${API_URL}${res.data.url}` });
                        toast.success('Photo uploaded!');
                      } catch (err) {
                        toast.error('Upload failed');
                      }
                    }}
                    className="sr-only"
                  />
                  <Upload className="w-6 h-6 text-muted-foreground mb-1" />
                  <span className="text-xs text-muted-foreground">Tap to add photo</span>
                </label>
              )}
            </div>

            {/* Lineage - Dam & Sire */}
            <div className="pt-2 border-t">
              <Label className="text-sm flex items-center gap-2 mb-2">
                <GitBranch className="w-4 h-4" />
                Lineage (Optional)
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Dam (Mother)</Label>
                  <select
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                    value={animalForm.dam_id}
                    onChange={(e) => setAnimalForm({ ...animalForm, dam_id: e.target.value })}
                    data-testid="foundation-dam-select"
                  >
                    <option value="">None / Unknown</option>
                    {getPotentialDams().map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name} {a.tag_number ? `(#${a.tag_number})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Sire (Father)</Label>
                  <select
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                    value={animalForm.sire_id}
                    onChange={(e) => setAnimalForm({ ...animalForm, sire_id: e.target.value })}
                    data-testid="foundation-sire-select"
                  >
                    <option value="">None / Unknown</option>
                    {getPotentialSires().map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name} {a.tag_number ? `(#${a.tag_number})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-sm">Notes</Label>
              <textarea
                className="w-full min-h-[60px] px-3 py-2 rounded-md border border-input bg-background resize-none text-sm"
                value={animalForm.notes}
                onChange={(e) => setAnimalForm({ ...animalForm, notes: e.target.value })}
                placeholder="Special traits, history, etc."
                data-testid="foundation-notes-input"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowAddDialog(false); setEditingAnimal(null); resetAnimalForm(); }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={editingAnimal ? handleUpdateAnimal : handleCreateAnimal}
                disabled={!animalForm.name}
                data-testid="save-foundation-btn"
              >
                {editingAnimal ? 'Update' : 'Add'} Animal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Animal Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedAnimal && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  {selectedAnimal.photo_url ? (
                    <img 
                      src={selectedAnimal.photo_url} 
                      alt={selectedAnimal.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-6 h-6 text-primary" />
                  )}
                  {selectedAnimal.name}
                  {getSexDisplay(selectedAnimal.sex)}
                  {selectedAnimal.status !== 'active' && getStatusBadge(selectedAnimal.status)}
                  <button
                    onClick={() => handleToggleFoundation(selectedAnimal)}
                    className={`ml-auto p-1.5 rounded-full transition-colors ${
                      selectedAnimal.is_foundation === true || selectedAnimal.is_foundation === undefined
                        ? 'text-amber-500 hover:bg-amber-500/10' 
                        : 'text-muted-foreground/40 hover:text-amber-500 hover:bg-amber-500/10'
                    }`}
                    title={selectedAnimal.is_foundation === true || selectedAnimal.is_foundation === undefined ? 'Remove from Foundation' : 'Add to Foundation'}
                    data-testid="detail-foundation-star"
                  >
                    <Star className={`w-5 h-5 ${selectedAnimal.is_foundation === true || selectedAnimal.is_foundation === undefined ? 'fill-current' : ''}`} />
                  </button>
                </DialogTitle>
              </DialogHeader>
              
              {/* Profile | Line View Toggle */}
              <Tabs value={activeView} onValueChange={(v) => {
                setActiveView(v);
                if (v === 'lineage') {
                  // Always fetch fresh lineage data when switching to Line View
                  fetchLineage(selectedAnimal.id);
                }
              }}>
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="profile">Profile</TabsTrigger>
                  <TabsTrigger value="lineage">Line View</TabsTrigger>
                </TabsList>

                <TabsContent value="profile" className="space-y-4 mt-0">
                  {/* Animal Info */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground">Breed</p>
                      <p className="font-medium">{selectedAnimal.breed || 'Unknown'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Tag #</p>
                      <p className="font-medium">{selectedAnimal.tag_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Purpose</p>
                      <p className="font-medium capitalize">{selectedAnimal.purpose}</p>
                    </div>
                    {selectedAnimal.birth_date && (
                      <div>
                        <p className="text-muted-foreground">Birth Date</p>
                        <p className="font-medium">{new Date(selectedAnimal.birth_date).toLocaleDateString()}</p>
                      </div>
                    )}
                  </div>

                  {/* Milk Stats (for dairy animals) */}
                  {(selectedAnimal.purpose === 'milk' || selectedAnimal.purpose === 'mixed') && selectedAnimal.sex === 'female' && (
                    <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Milk className="w-4 h-4 text-blue-600" />
                        <span className="font-medium text-sm text-blue-800">Milk Production</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-blue-600">30-Day Total</p>
                          <p className="font-semibold text-blue-800">
                            {selectedAnimal.milk_30d_total?.toFixed(1) || 0} gal
                          </p>
                        </div>
                        <div>
                          <p className="text-blue-600">Lifetime Total</p>
                          <p className="font-semibold text-blue-800">
                            {selectedAnimal.milk_lifetime_total?.toFixed(1) || 0} gal
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedAnimal.notes && (
                    <div className="p-3 rounded-lg bg-muted/30 text-sm">
                      <p className="text-muted-foreground text-xs mb-1">Notes</p>
                      <p>{selectedAnimal.notes}</p>
                    </div>
                  )}

                  {/* Multi-photo gallery */}
                  <AnimalPhotoGallery
                    animalId={selectedAnimal.id}
                    animalKind="foundation"
                    animalName={selectedAnimal.name}
                    animalType={selectedAnimal.animal_type}
                  />

                  {/* Quick Lineage Preview */}
                  <div className="p-3 rounded-lg bg-muted/20 text-sm">
                    <p className="text-muted-foreground text-xs mb-2">Lineage</p>
                    <div className="flex gap-4">
                      <div>
                        <span className="text-muted-foreground">Dam: </span>
                        <span className="font-medium">
                          {selectedAnimal.dam?.name || 'Unknown'}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Sire: </span>
                        <span className="font-medium">
                          {selectedAnimal.sire?.name || 'Unknown'}
                        </span>
                      </div>
                    </div>
                    {selectedAnimal.offspring?.length > 0 && (
                      <p className="mt-1 text-muted-foreground">
                        {selectedAnimal.offspring.length} offspring
                      </p>
                    )}
                  </div>

                  {/* Quick Actions Grid */}
                  <div className="grid grid-cols-4 gap-2">
                    {selectedAnimal.sex === 'female' && (
                      <Button 
                        variant="outline" 
                        className="flex-col h-auto py-3 text-blue-600 border-blue-200 hover:bg-blue-50"
                        onClick={() => setShowQuickMilkDialog(true)}
                        data-testid="quick-milk-btn"
                      >
                        <Milk className="w-5 h-5 mb-1" />
                        <span className="text-xs">Log Milk</span>
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      className="flex-col h-auto py-3 text-green-600 border-green-200 hover:bg-green-50"
                      onClick={() => setShowQuickWeightDialog(true)}
                      data-testid="quick-weight-btn"
                    >
                      <Scale className="w-5 h-5 mb-1" />
                      <span className="text-xs">Weight</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-col h-auto py-3 text-purple-600 border-purple-200 hover:bg-purple-50"
                      onClick={() => setShowQuickPhotoDialog(true)}
                      data-testid="quick-photo-btn"
                    >
                      <Camera className="w-5 h-5 mb-1" />
                      <span className="text-xs">Photo</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-col h-auto py-3 text-amber-600 border-amber-200 hover:bg-amber-50"
                      onClick={() => setShowQuickNoteDialog(true)}
                      data-testid="quick-note-btn"
                    >
                      <FileText className="w-5 h-5 mb-1" />
                      <span className="text-xs">Note</span>
                    </Button>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { setShowDetailDialog(false); openEditDialog(selectedAnimal); }}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      size="sm"
                      onClick={() => setShowEventDialog(true)}
                      data-testid="add-foundation-event-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Log Event
                    </Button>
                    {/* Breeding & Birth Actions - Only for females */}
                    {selectedAnimal.sex === 'female' && (
                      <>
                        <Button 
                          size="sm"
                          variant="outline"
                          className="border-purple-200 text-purple-700 hover:bg-purple-50"
                          onClick={() => { setShowDetailDialog(false); openBreedingDialogForDam(selectedAnimal); }}
                          data-testid="log-breeding-btn"
                        >
                          <Heart className="w-4 h-4 mr-1" />
                          Log Breeding
                        </Button>
                        <Button 
                          size="sm"
                          variant="outline"
                          className="border-pink-200 text-pink-700 hover:bg-pink-50"
                          onClick={() => { setShowDetailDialog(false); openBirthDialogForDam(selectedAnimal); }}
                          data-testid="log-birth-btn"
                        >
                          <Baby className="w-4 h-4 mr-1" />
                          Log Birth
                        </Button>
                        <Button 
                          size="sm"
                          variant="ghost"
                          className="text-muted-foreground"
                          onClick={() => { setShowDetailDialog(false); fetchDamBirthHistory(selectedAnimal.id); }}
                          data-testid="birth-history-btn"
                        >
                          <Calendar className="w-4 h-4 mr-1" />
                          History
                        </Button>
                      </>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="ml-auto text-destructive hover:text-destructive"
                      onClick={() => handleDeleteAnimal(selectedAnimal.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Event History */}
                  <div>
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                      <ClipboardList className="w-4 h-4" />
                      Event History
                    </h4>
                    {!selectedAnimal.events || selectedAnimal.events.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No events recorded yet
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {selectedAnimal.events.map((event) => {
                          const et = EVENT_TYPES.find(e => e.value === event.event_type);
                          const EventIcon = et?.icon || ClipboardList;
                          return (
                            <div 
                              key={event.id}
                              className="flex items-start gap-3 p-2 rounded-lg bg-muted/30"
                            >
                              <div className={`p-1.5 rounded ${et?.color || 'bg-gray-100 text-gray-700'}`}>
                                <EventIcon className="w-3 h-3" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium capitalize">{event.event_type}</span>
                                  {event.value && (
                                    <span className="text-xs text-muted-foreground">
                                      {event.value} {event.unit}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm truncate">{event.description}</p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(event.date).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="lineage" className="mt-0">
                  <LineageTree data={lineageData} />
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Log Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-sm">Event Type</Label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {EVENT_TYPES.map((et) => (
                  <button
                    key={et.value}
                    type="button"
                    className={`p-2 rounded-lg border text-xs text-center transition-colors ${
                      eventForm.event_type === et.value 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => setEventForm({ ...eventForm, event_type: et.value })}
                  >
                    <et.icon className="w-4 h-4 mx-auto mb-1" />
                    {et.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm">Description *</Label>
              <Input
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                placeholder="What happened?"
                className="h-9"
                data-testid="foundation-event-description"
              />
            </div>

            {eventForm.event_type === 'weight' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Weight</Label>
                  <Input
                    type="number"
                    value={eventForm.value}
                    onChange={(e) => setEventForm({ ...eventForm, value: e.target.value })}
                    placeholder="0"
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-sm">Unit</Label>
                  <Input
                    value={eventForm.unit || 'lbs'}
                    onChange={(e) => setEventForm({ ...eventForm, unit: e.target.value })}
                    placeholder="lbs"
                    className="h-9"
                  />
                </div>
              </div>
            )}

            <div>
              <Label className="text-sm">Date</Label>
              <Input
                type="date"
                value={eventForm.date}
                onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                className="h-9"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowEventDialog(false); resetEventForm(); }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleAddEvent}
                disabled={!eventForm.description}
                data-testid="save-foundation-event-btn"
              >
                Log Event
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* BIRTH EVENT DIALOG */}
      <Dialog open={showBirthDialog} onOpenChange={(open) => { setShowBirthDialog(open); if (!open) resetBirthForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Baby className="w-5 h-5 text-pink-500" />
              Log Birth Event
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Dam & Sire Selection */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Mother (Dam) *</Label>
                <select
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  value={birthForm.dam_id}
                  onChange={(e) => setBirthForm({ ...birthForm, dam_id: e.target.value })}
                  data-testid="birth-dam-select"
                >
                  <option value="">Select mother...</option>
                  {getPotentialDams().filter(a => a.status === 'active').map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name} {a.tag_number ? `(#${a.tag_number})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-sm">Father (Sire)</Label>
                <select
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  value={birthForm.sire_id}
                  onChange={(e) => setBirthForm({ ...birthForm, sire_id: e.target.value })}
                  data-testid="birth-sire-select"
                >
                  <option value="">Unknown / Not specified</option>
                  {getPotentialSires().map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name} {a.tag_number ? `(#${a.tag_number})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Birth Date *</Label>
                <Input
                  type="date"
                  value={birthForm.date}
                  onChange={(e) => setBirthForm({ ...birthForm, date: e.target.value })}
                  className="h-9"
                  data-testid="birth-date-input"
                />
              </div>
              <div>
                <Label className="text-sm">Time (optional)</Label>
                <Input
                  type="time"
                  value={birthForm.time}
                  onChange={(e) => setBirthForm({ ...birthForm, time: e.target.value })}
                  className="h-9"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-sm">Notes (optional)</Label>
              <textarea
                className="w-full min-h-[60px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-none"
                value={birthForm.notes}
                onChange={(e) => setBirthForm({ ...birthForm, notes: e.target.value })}
                placeholder="Weather, any complications, assistance needed..."
              />
            </div>

            {/* Babies Section */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Baby className="w-4 h-4" />
                  Babies ({birthForm.babies.length})
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addBabyEntry}
                  data-testid="add-baby-btn"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Baby
                </Button>
              </div>

              <div className="space-y-4">
                {birthForm.babies.map((baby, index) => {
                  const outcomeInfo = BABY_OUTCOMES.find(o => o.value === baby.outcome);
                  const allowsProfile = outcomeInfo?.allowProfile ?? false;
                  
                  return (
                    <div 
                      key={index} 
                      className="p-3 rounded-lg border bg-muted/20 space-y-3"
                      data-testid={`baby-entry-${index}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Baby #{index + 1}</span>
                        {birthForm.babies.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                            onClick={() => removeBabyEntry(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>

                      {/* Outcome - Required */}
                      <div>
                        <Label className="text-xs text-muted-foreground">Outcome *</Label>
                        <select
                          className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                          value={baby.outcome}
                          onChange={(e) => updateBabyEntry(index, 'outcome', e.target.value)}
                          data-testid={`baby-outcome-${index}`}
                        >
                          {BABY_OUTCOMES.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Optional Fields */}
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Sex</Label>
                          <select
                            className="w-full h-8 px-2 rounded-md border border-input bg-background text-sm"
                            value={baby.sex}
                            onChange={(e) => updateBabyEntry(index, 'sex', e.target.value)}
                          >
                            <option value="">Unknown</option>
                            <option value="male">♂ Male</option>
                            <option value="female">♀ Female</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Birth Weight</Label>
                          <Input
                            type="number"
                            step="0.1"
                            className="h-8"
                            placeholder="0.0"
                            value={baby.birth_weight}
                            onChange={(e) => updateBabyEntry(index, 'birth_weight', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Unit</Label>
                          <select
                            className="w-full h-8 px-2 rounded-md border border-input bg-background text-sm"
                            value={baby.weight_unit}
                            onChange={(e) => updateBabyEntry(index, 'weight_unit', e.target.value)}
                          >
                            <option value="lbs">lbs</option>
                            <option value="kg">kg</option>
                            <option value="oz">oz</option>
                          </select>
                        </div>
                      </div>

                      {/* Baby Notes */}
                      <div>
                        <Label className="text-xs text-muted-foreground">Notes</Label>
                        <Input
                          className="h-8"
                          placeholder="Any observations..."
                          value={baby.notes}
                          onChange={(e) => updateBabyEntry(index, 'notes', e.target.value)}
                        />
                      </div>

                      {/* Create Profile Toggle - Only for livable outcomes */}
                      {allowsProfile && (
                        <div className="flex items-center gap-3 pt-2 border-t border-dashed">
                          <input
                            type="checkbox"
                            id={`create-profile-${index}`}
                            checked={baby.create_profile}
                            onChange={(e) => updateBabyEntry(index, 'create_profile', e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300"
                          />
                          <label htmlFor={`create-profile-${index}`} className="text-sm">
                            Create animal profile
                          </label>
                        </div>
                      )}

                      {/* Name field if creating profile */}
                      {allowsProfile && baby.create_profile && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Name for new animal</Label>
                          <Input
                            className="h-8"
                            placeholder="Leave blank for auto-name"
                            value={baby.name}
                            onChange={(e) => updateBabyEntry(index, 'name', e.target.value)}
                          />
                        </div>
                      )}

                      {/* Info for non-livable outcomes */}
                      {!allowsProfile && (
                        <p className="text-xs text-muted-foreground italic">
                          This outcome is recorded for statistics only.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2 border-t">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowBirthDialog(false); resetBirthForm(); }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleCreateBirthEvent}
                disabled={!birthForm.dam_id || !birthForm.date}
                data-testid="save-birth-event-btn"
              >
                <Baby className="w-4 h-4 mr-1" />
                Save Birth Event
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* BIRTH HISTORY DIALOG */}
      <Dialog open={showBirthHistoryDialog} onOpenChange={setShowBirthHistoryDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Calendar className="w-5 h-5 text-pink-500" />
              Birth History
            </DialogTitle>
          </DialogHeader>
          
          {damBirthHistory && (
            <div className="space-y-4 mt-2">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-pink-50 text-center">
                  <p className="text-2xl font-bold text-pink-700">{damBirthHistory.stats?.total_births || 0}</p>
                  <p className="text-xs text-pink-600">Births</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50 text-center">
                  <p className="text-2xl font-bold text-blue-700">{damBirthHistory.stats?.total_babies || 0}</p>
                  <p className="text-xs text-blue-600">Total Babies</p>
                </div>
                <div className="p-3 rounded-lg bg-green-50 text-center">
                  <p className="text-2xl font-bold text-green-700">
                    {damBirthHistory.stats?.outcomes?.thriving_with_mom || 0}
                  </p>
                  <p className="text-xs text-green-600">Thriving</p>
                </div>
              </div>

              {/* Outcome Breakdown */}
              {damBirthHistory.stats?.outcomes && Object.keys(damBirthHistory.stats.outcomes).length > 0 && (
                <div className="p-3 rounded-lg bg-muted/30">
                  <p className="text-sm font-medium mb-2">Outcome Summary</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(damBirthHistory.stats.outcomes).map(([outcome, count]) => (
                      <span key={outcome} className="text-xs">
                        {getOutcomeDisplay(outcome)} × {count}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Birth Events List */}
              <div>
                <p className="text-sm font-medium mb-2">Birth Events</p>
                {!damBirthHistory.events || damBirthHistory.events.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No birth events recorded
                  </p>
                ) : (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {damBirthHistory.events.map((event) => (
                      <div 
                        key={event.id}
                        className="p-3 rounded-lg border bg-card"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">
                            {new Date(event.date).toLocaleDateString()}
                            {event.time && ` at ${event.time}`}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {event.baby_count} {event.baby_count === 1 ? 'baby' : 'babies'}
                          </span>
                        </div>
                        
                        {event.sire_name && (
                          <p className="text-xs text-muted-foreground mb-2">
                            Sire: {event.sire_name}
                          </p>
                        )}
                        
                        <div className="space-y-1">
                          {event.babies?.map((baby, i) => (
                            <div key={baby.id || i} className="flex items-center gap-2 text-sm">
                              <span className="text-muted-foreground">#{i + 1}</span>
                              {getOutcomeDisplay(baby.outcome)}
                              {baby.sex && baby.sex !== 'unknown' && (
                                <span className={baby.sex === 'female' ? 'text-pink-500' : 'text-blue-500'}>
                                  {baby.sex === 'female' ? '♀' : '♂'}
                                </span>
                              )}
                              {baby.birth_weight && (
                                <span className="text-muted-foreground">
                                  {baby.birth_weight} {baby.weight_unit}
                                </span>
                              )}
                              {baby.created_animal_id && (
                                <span className="text-xs text-green-600">✓ Profile created</span>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {event.notes && (
                          <p className="text-xs text-muted-foreground mt-2 italic">{event.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowBirthHistoryDialog(false)}
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* BREEDING EVENT DIALOG */}
      <Dialog open={showBreedingDialog} onOpenChange={(open) => { setShowBreedingDialog(open); if (!open) resetBreedingForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Heart className="w-5 h-5 text-purple-500" />
              Log Breeding
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Dam Selection */}
            <div>
              <Label className="text-sm">Female Being Bred *</Label>
              <select
                className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                value={breedingForm.dam_id}
                onChange={(e) => setBreedingForm({ ...breedingForm, dam_id: e.target.value })}
                data-testid="breeding-dam-select"
              >
                <option value="">Select female...</option>
                {getPotentialDams().filter(a => a.status === 'active').map(a => (
                  <option key={a.id} value={a.id}>
                    {a.name} {a.tag_number ? `(#${a.tag_number})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Sire Selection */}
            <div>
              <Label className="text-sm">Sire (Male)</Label>
              <select
                className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                value={breedingForm.sire_id}
                onChange={(e) => setBreedingForm({ ...breedingForm, sire_id: e.target.value })}
                data-testid="breeding-sire-select"
              >
                <option value="">Unknown / Not specified</option>
                {getPotentialSires().map(a => (
                  <option key={a.id} value={a.id}>
                    {a.name} {a.tag_number ? `(#${a.tag_number})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Date */}
            <div>
              <Label className="text-sm">Breeding Date *</Label>
              <Input
                type="date"
                value={breedingForm.date}
                onChange={(e) => setBreedingForm({ ...breedingForm, date: e.target.value })}
                className="h-9"
                data-testid="breeding-date-input"
              />
            </div>

            {/* Confirmed toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="breeding-confirmed"
                checked={breedingForm.confirmed}
                onChange={(e) => setBreedingForm({ ...breedingForm, confirmed: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300"
              />
              <label htmlFor="breeding-confirmed" className="text-sm">
                Pregnancy confirmed
              </label>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-sm">Notes (optional)</Label>
              <textarea
                className="w-full min-h-[60px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-none"
                value={breedingForm.notes}
                onChange={(e) => setBreedingForm({ ...breedingForm, notes: e.target.value })}
                placeholder="Observed behavior, breeding conditions..."
              />
            </div>

            {/* Due Date Preview */}
            {breedingForm.date && (
              <div className="p-3 rounded-lg bg-purple-50 border border-purple-200">
                <p className="text-sm text-purple-700">
                  <span className="font-medium">Expected due date:</span>{' '}
                  {new Date(new Date(breedingForm.date).getTime() + (
                    animalType === 'cattle' ? 283 : animalType === 'sheep' ? 147 : 150
                  ) * 24 * 60 * 60 * 1000).toLocaleDateString()}
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  Based on {animalType === 'cattle' ? '~283' : animalType === 'sheep' ? '~147' : '~150'} day gestation
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowBreedingDialog(false); resetBreedingForm(); }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleCreateBreedingEvent}
                disabled={!breedingForm.dam_id || !breedingForm.date}
                data-testid="save-breeding-event-btn"
              >
                <Heart className="w-4 h-4 mr-1" />
                Save Breeding
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QUICK MILK LOG DIALOG */}
      <Dialog open={showQuickMilkDialog} onOpenChange={setShowQuickMilkDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Milk className="w-5 h-5 text-blue-500" />
              Log Milk - {selectedAnimal?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={quickMilkForm.date}
                onChange={(e) => setQuickMilkForm({ ...quickMilkForm, date: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="0.0"
                  value={quickMilkForm.amount}
                  onChange={(e) => setQuickMilkForm({ ...quickMilkForm, amount: e.target.value })}
                />
              </div>
              <div>
                <Label>Unit</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                  value={quickMilkForm.unit}
                  onChange={(e) => setQuickMilkForm({ ...quickMilkForm, unit: e.target.value })}
                >
                  <option value="gallons">Gallons</option>
                  <option value="liters">Liters</option>
                  <option value="quarts">Quarts</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                placeholder="Optional notes..."
                value={quickMilkForm.notes}
                onChange={(e) => setQuickMilkForm({ ...quickMilkForm, notes: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowQuickMilkDialog(false)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleQuickMilkLog} disabled={!quickMilkForm.amount}>
                <Milk className="w-4 h-4 mr-1" />
                Log Milk
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QUICK WEIGHT LOG DIALOG */}
      <Dialog open={showQuickWeightDialog} onOpenChange={setShowQuickWeightDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Scale className="w-5 h-5 text-green-500" />
              Log Weight - {selectedAnimal?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={quickWeightForm.date}
                onChange={(e) => setQuickWeightForm({ ...quickWeightForm, date: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Weight *</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="0.0"
                  value={quickWeightForm.weight}
                  onChange={(e) => setQuickWeightForm({ ...quickWeightForm, weight: e.target.value })}
                />
              </div>
              <div>
                <Label>Unit</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                  value={quickWeightForm.unit}
                  onChange={(e) => setQuickWeightForm({ ...quickWeightForm, unit: e.target.value })}
                >
                  <option value="lbs">lbs</option>
                  <option value="kg">kg</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                placeholder="Condition, feeding notes..."
                value={quickWeightForm.notes}
                onChange={(e) => setQuickWeightForm({ ...quickWeightForm, notes: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowQuickWeightDialog(false)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleQuickWeightLog} disabled={!quickWeightForm.weight}>
                <Scale className="w-4 h-4 mr-1" />
                Log Weight
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QUICK PHOTO ADD DIALOG */}
      <Dialog open={showQuickPhotoDialog} onOpenChange={(open) => {
        setShowQuickPhotoDialog(open);
        if (!open) {
          setPhotoPreview(null);
          setQuickPhotoForm({ photo_url: '', date: todayLocal(), notes: '' });
        }
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Camera className="w-5 h-5 text-purple-500" />
              Add Photo - {selectedAnimal?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={quickPhotoForm.date}
                onChange={(e) => setQuickPhotoForm({ ...quickPhotoForm, date: e.target.value })}
              />
            </div>
            
            {/* Photo Upload Section */}
            <div>
              <Label>Photo *</Label>
              
              {/* Upload Button / Preview */}
              {(photoPreview || quickPhotoForm.photo_url) ? (
                <div className="relative rounded-lg overflow-hidden border mt-2">
                  <img 
                    src={photoPreview || quickPhotoForm.photo_url} 
                    alt="Preview" 
                    className="w-full h-40 object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  {photoUploading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Loader2 className="w-8 h-8 text-white animate-spin" />
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setPhotoPreview(null);
                      setQuickPhotoForm(prev => ({ ...prev, photo_url: '' }));
                    }}
                    className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full text-white hover:bg-black/80 transition-colors"
                    type="button"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label 
                  htmlFor="photo-upload-input" 
                  className="flex flex-col items-center justify-center w-full h-32 mt-2 border-2 border-dashed border-muted-foreground/30 rounded-lg cursor-pointer hover:border-primary/50 hover:bg-muted/20 transition-colors active:bg-muted/30"
                  onClick={() => document.getElementById('photo-upload-input')?.click()}
                >
                  <input
                    id="photo-upload-input"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoFileSelect}
                    className="sr-only"
                    data-testid="photo-file-input"
                  />
                  <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                  <span className="text-sm text-muted-foreground">Tap to select photo</span>
                  <span className="text-xs text-muted-foreground/70 mt-1">or take a new one</span>
                </label>
              )}
            </div>
            
            <div>
              <Label>Notes</Label>
              <Input
                placeholder="What's happening in this photo..."
                value={quickPhotoForm.notes}
                onChange={(e) => setQuickPhotoForm({ ...quickPhotoForm, notes: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowQuickPhotoDialog(false)}>
                Cancel
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleQuickPhotoAdd} 
                disabled={!quickPhotoForm.photo_url || photoUploading}
              >
                {photoUploading ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 mr-1" />
                )}
                {photoUploading ? 'Uploading...' : 'Add Photo'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QUICK NOTE ADD DIALOG */}
      <Dialog open={showQuickNoteDialog} onOpenChange={setShowQuickNoteDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-500" />
              Add Note - {selectedAnimal?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={quickNoteForm.date}
                onChange={(e) => setQuickNoteForm({ ...quickNoteForm, date: e.target.value })}
              />
            </div>
            <div>
              <Label>Note *</Label>
              <textarea
                className="w-full min-h-[100px] px-3 py-2 rounded-md border border-input bg-background resize-none"
                placeholder="Behavior, health observations, milestones..."
                value={quickNoteForm.notes}
                onChange={(e) => setQuickNoteForm({ ...quickNoteForm, notes: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowQuickNoteDialog(false)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleQuickNoteAdd} disabled={!quickNoteForm.notes}>
                <FileText className="w-4 h-4 mr-1" />
                Add Note
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FoundationStock;
